import type { TabularConfigBuildPublicReason } from '@axilon-apps/domains-ontology-ui-state'
import { Card } from '../../../components/ui/card'
import { cn } from '../../../lib/utils'
import { CHIP, CHIP_WARN, MONO_WRAP, SECTION_LABEL } from '../../IngestPage/shared/compact'
import { changedSections, fingerprintCellChanges } from '../util/draftChanges'
import type { ConfigSubject } from '../util/subject'

/**
 * The header cells the fingerprint pins, one chip per cell so a wide header
 * wraps instead of scrolling. Cells that differ from the compared version are
 * marked; the file-side diff of a mismatch rebuild sits on top.
 */
export function FingerprintTab({
  subject,
  reason
}: {
  subject: ConfigSubject
  /** The build's reason, when the subject is a draft: a fingerprint mismatch carries the file-side diff. */
  reason?: TabularConfigBuildPublicReason
}) {
  const fingerprint = subject.script?.fingerprint
  if (!fingerprint) {
    return <p className="text-xs text-ink-3">An agent parser has no fingerprint.</p>
  }
  const changes = fingerprintCellChanges(subject.diff)
  const changedCells = new Set(changes.map((change) => `${change.sheet}:${change.cell}`))
  // A sheet added or dropped, or CSV ↔ workbook, changes the fingerprint without a per-cell entry.
  const changedBeyondCells = changedSections(subject.diff).has('fingerprint')
  return (
    <div className="flex min-w-0 flex-col gap-3">
      {subject.compareLabel ? (
        <p className="text-11 text-ink-3" data-testid="change-note-fingerprint">
          {changes.length ? (
            <span className="text-st-warn">
              {changes.length} cell{changes.length === 1 ? '' : 's'} differ from{' '}
              {subject.compareLabel}. Everything else in the fingerprint is unchanged.
            </span>
          ) : changedBeyondCells ? (
            <span className="text-st-warn">
              The fingerprint's layout (its sheets or kind) changed from {subject.compareLabel}; the
              cells below are the new one's.
            </span>
          ) : (
            <span>Fingerprint: unchanged from {subject.compareLabel}</span>
          )}
        </p>
      ) : (
        <p className="text-11 text-ink-3">
          A file runs with this version only when its header row matches these cells; otherwise the
          job holds and a rebuild proposes a new draft.
        </p>
      )}
      {reason?.kind === 'fingerprint_mismatch' ? (
        <Card className="gap-1.5 p-3" data-testid="fingerprint-file-diff">
          <p className={SECTION_LABEL}>In the file that started this rebuild</p>
          <ul className={cn(MONO_WRAP, 'flex flex-col gap-0.5 text-11 leading-5')}>
            {reason.diff.map((entry, index) => (
              <li key={index}>
                {entry.path} was{' '}
                <code className="text-st-down">{entry.expected ?? '(missing)'}</code>, now{' '}
                <code className="text-st-run">{entry.found ?? '(missing)'}</code>
              </li>
            ))}
          </ul>
        </Card>
      ) : null}
      {changes.length ? (
        <ul
          className={cn(MONO_WRAP, 'flex flex-col gap-0.5 text-11 leading-5')}
          data-testid="fingerprint-cell-changes"
        >
          {changes.map((change, index) => (
            <li key={index}>
              {change.sheet} · cell {change.cell + 1}:{' '}
              <code className="text-st-down line-through">{change.before ?? '(missing)'}</code> →{' '}
              <code className="text-st-run">{change.after ?? '(missing)'}</code>
            </li>
          ))}
        </ul>
      ) : null}
      {fingerprint.kind === 'csv' ? (
        <Card className="gap-2 p-3">
          <p className={SECTION_LABEL}>CSV header</p>
          <Cells
            cells={fingerprint.header}
            changed={(index) => changedCells.has(`header:${index}`)}
          />
        </Card>
      ) : (
        fingerprint.sheets.map((sheet) => {
          const header = fingerprint.headers[sheet]
          return (
            <Card
              key={sheet}
              className="min-w-0 gap-2 p-3"
              data-testid={`fingerprint-sheet-${sheet}`}
            >
              <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                <h3 className="font-mono text-xs font-semibold">{sheet}</h3>
                <span className="text-11 text-ink-3">
                  {header ? `header row ${header.row}` : 'no header recorded'}
                  {header?.variableAfter !== undefined
                    ? ` · first ${header.variableAfter} columns compared`
                    : ''}
                  {header?.excludeColumns?.length
                    ? ` · columns ${header.excludeColumns.join(', ')} ignored`
                    : ''}
                </span>
              </div>
              {header ? (
                <Cells
                  cells={header.cells}
                  changed={(index) => changedCells.has(`${sheet}:${index}`)}
                />
              ) : null}
            </Card>
          )
        })
      )}
    </div>
  )
}

function Cells({ cells, changed }: { cells: string[]; changed: (index: number) => boolean }) {
  return (
    <ol className="flex flex-wrap gap-1" aria-label="Header cells">
      {cells.map((cell, index) => (
        <li key={index} className={cn(CHIP, 'font-mono', changed(index) && CHIP_WARN)}>
          <span className="text-10 tabular-nums text-ink-3 select-none">{index + 1}</span>
          <span className="break-all">{cell === '' ? '(blank)' : cell}</span>
        </li>
      ))}
    </ol>
  )
}
